import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Iconify from "src/components/iconify";
import {
  useGetTenantInformationQuery,
  useUpdateTenantMailSettingsMutation,
} from "src/features/tenants/tenantsApiSlice";

// Beschreibung wann jede automatische Mail gesendet wird
const MAIL_META = {
  registrationLink: {
    label: "Registrierungslink",
    icon: "mdi:email-fast-outline",
    when: 'Wird gesendet, wenn ein Admin über "Registrierungslink senden" einen persönlichen Aktivierungslink an Mitglieder verschickt. Die Gültigkeit wird direkt im Versanddialog festgelegt.',
    path: "/dashboard/settings/mail/registration-link-template",
    enabledKey: "registrationLinkEnabled",
  },
  registration: {
    label: "Anmeldebestätigung",
    icon: "ic:baseline-description",
    when: "Wird automatisch gesendet, wenn ein Admin ein neues Mitglied manuell anlegt oder einem bestehenden Vertrag hinzufügt.",
    path: "/dashboard/settings/mail/registration-template",
    enabledKey: "registrationEnabled",
  },
  termination: {
    label: "Kündigungsbestätigung",
    icon: "ic:baseline-cancel",
    when: "Wird automatisch gesendet, wenn eine Kündigung für ein Mitglied eingetragen wird.",
    path: "/dashboard/settings/mail/termination-template",
    enabledKey: "terminationEnabled",
  },
  selfRegReceipt: {
    label: "Selbstanmeldung – Eingangsbestätigung",
    icon: "ic:baseline-mark-email-read",
    when: "Wird automatisch gesendet, sobald eine öffentliche Selbstanmeldung eingereicht wurde (vor der Admin-Prüfung).",
    path: "/dashboard/settings/mail/selfreg-receipt-template",
    enabledKey: "selfRegReceiptEnabled",
  },
  selfRegApproval: {
    label: "Selbstanmeldung – Genehmigung",
    icon: "ic:baseline-check-circle",
    when: "Wird automatisch gesendet, wenn ein Admin eine Selbstanmeldung genehmigt. Enthält die Anmeldebestätigung als PDF-Anhang.",
    path: "/dashboard/settings/mail/selfreg-approval-template",
    enabledKey: "selfRegApprovalEnabled",
  },
  selfRegRejection: {
    label: "Selbstanmeldung – Ablehnung",
    icon: "ic:baseline-do-not-disturb",
    when: "Wird automatisch gesendet, wenn ein Admin eine Selbstanmeldung ablehnt.",
    path: "/dashboard/settings/mail/selfreg-rejection-template",
    enabledKey: "selfRegRejectionEnabled",
  },
};

const TEMPLATE_ORDER = [
  "registrationLink",
  "registration",
  "termination",
  "selfRegReceipt",
  "selfRegApproval",
  "selfRegRejection",
];

// Erkennt, ob der gespeicherte Wert ein vom Admin angepasster Text ist.
// Wir betrachten einen leeren String als „Standard-Vorlage" (Backend-Fallback greift).
const isCustomTemplate = (value) => Boolean((value || "").trim());

const MailSettings = () => {
  const navigate = useNavigate();
  const { data: tenantInfo, isLoading } = useGetTenantInformationQuery();
  const [updateTenantMailSettings] = useUpdateTenantMailSettingsMutation();

  const mailSettings = tenantInfo?.settings?.mail || {};
  const senderName =
    mailSettings.fromName ||
    tenantInfo?.tenantName ||
    "Kein Absendername hinterlegt";
  const senderMail =
    mailSettings.fromMail ||
    mailSettings.user ||
    "Keine Absender-Mail hinterlegt";

  // Local state for all toggles (logoInEmails + per-template enabled flags)
  const [logoInEmails, setLogoInEmails] = useState(false);
  const [enabledFlags, setEnabledFlags] = useState({
    registrationEnabled: true,
    terminationEnabled: true,
    selfRegReceiptEnabled: true,
    selfRegApprovalEnabled: true,
    selfRegRejectionEnabled: true,
    registrationLinkEnabled: true,
  });

  useEffect(() => {
    setLogoInEmails(Boolean(mailSettings.includeLogoInEmails));
    setEnabledFlags({
      registrationEnabled: mailSettings.registrationEnabled !== false,
      terminationEnabled: mailSettings.terminationEnabled !== false,
      selfRegReceiptEnabled: mailSettings.selfRegReceiptEnabled !== false,
      selfRegApprovalEnabled: mailSettings.selfRegApprovalEnabled !== false,
      selfRegRejectionEnabled: mailSettings.selfRegRejectionEnabled !== false,
      registrationLinkEnabled: mailSettings.registrationLinkEnabled !== false,
    });
  }, [
    mailSettings.includeLogoInEmails,
    mailSettings.registrationEnabled,
    mailSettings.terminationEnabled,
    mailSettings.selfRegReceiptEnabled,
    mailSettings.selfRegApprovalEnabled,
    mailSettings.selfRegRejectionEnabled,
    mailSettings.registrationLinkEnabled,
  ]);

  const handleLogoToggle = async (event) => {
    const nextValue = event.target.checked;
    setLogoInEmails(nextValue);
    try {
      await updateTenantMailSettings({
        includeLogoInEmails: nextValue,
      }).unwrap();
    } catch {
      setLogoInEmails(!nextValue);
    }
  };

  const handleEnabledToggle = async (key, nextValue) => {
    setEnabledFlags((prev) => ({ ...prev, [key]: nextValue }));
    try {
      await updateTenantMailSettings({ [key]: nextValue }).unwrap();
    } catch {
      setEnabledFlags((prev) => ({ ...prev, [key]: !nextValue }));
    }
  };

  // Hilfsfunktion: Welches Template-Feld gehört zu welchem Typ?
  const getTemplateKey = (type) => {
    const map = {
      registration: "registrationTemplate",
      termination: "terminationTemplate",
      selfRegReceipt: "selfRegReceiptTemplate",
      selfRegApproval: "selfRegApprovalTemplate",
      selfRegRejection: "selfRegRejectionTemplate",
      registrationLink: "registrationLinkTemplate",
    };
    return map[type];
  };

  if (isLoading) return null;

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 2 }}>
        E-Mail Versand
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Absender-Konfiguration */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Absender-Adresse"
          subheader="Ausgehende E-Mails werden über Ihre eigene Adresse versendet."
          avatar={
            <Iconify
              icon="ic:baseline-settings"
              sx={{ color: "text.secondary" }}
            />
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Box
            onClick={() => navigate("/dashboard/settings/mail/config")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1.5,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              cursor: "pointer",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "primary.lighter",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <Iconify
                icon="ic:baseline-email"
                sx={{ color: "primary.main" }}
              />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight="bold" noWrap>
                {senderName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {senderMail}
              </Typography>
            </Box>
            <Iconify
              icon="mdi:chevron-right"
              sx={{ color: "text.secondary" }}
            />
          </Box>

          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                checked={logoInEmails}
                onChange={handleLogoToggle}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  Logo in E-Mails anzeigen
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ihr Vereinslogo wird automatisch am Ende jeder E-Mail
                  eingefügt.
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      {/* Automatische E-Mail-Vorlagen */}
      <Card>
        <CardHeader
          title="Automatische E-Mail-Vorlagen"
          subheader="Konfigurieren Sie Betreff und Text jeder automatisch versendeten E-Mail. Deaktivierte Mails werden vom System nicht gesendet."
          avatar={
            <Iconify
              icon="mdi:email-newsletter"
              sx={{ color: "text.secondary" }}
            />
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Standard-Vorlage:</strong> Wenn keine eigene Vorlage
            hinterlegt ist, verwendet das System automatisch einen sinnvollen
            Standardtext.
          </Alert>

          <List disablePadding>
            {TEMPLATE_ORDER.map((type, index) => {
              const meta = MAIL_META[type];
              const templateKey = getTemplateKey(type);
              const isEnabled = enabledFlags[meta.enabledKey];
              const hasCustomTemplate = isCustomTemplate(
                mailSettings[templateKey],
              );

              return (
                <React.Fragment key={type}>
                  {index > 0 && <Divider sx={{ my: 0.5 }} />}
                  <ListItem
                    disableGutters
                    onClick={() => navigate(meta.path)}
                    sx={{
                      py: 1,
                      px: 1.5,
                      mx: -1.5,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: 1,
                      opacity: isEnabled ? 1 : 0.55,
                      cursor: "pointer",
                      borderRadius: 1.5,
                      borderLeft: "3px solid transparent",
                      transition:
                        "background-color 0.15s ease, border-color 0.15s ease",
                      "&:hover": {
                        bgcolor: "action.hover",
                        borderLeftColor: "primary.main",
                        "& .row-nav-icon": {
                          color: "primary.main",
                          transform: "translateX(3px)",
                        },
                      },
                    }}
                  >
                    {/* Icon + Label + Beschreibung */}
                    <ListItemIcon sx={{ minWidth: 36, flexShrink: 0 }}>
                      <Iconify
                        icon={meta.icon}
                        sx={{
                          color: isEnabled ? "primary.main" : "text.disabled",
                          fontSize: 22,
                        }}
                      />
                    </ListItemIcon>

                    <ListItemText
                      sx={{ flexGrow: 1, mr: 1 }}
                      primary={
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {meta.label}
                          </Typography>
                          {hasCustomTemplate ? (
                            <Chip
                              label="Angepasst"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Standard"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {!isEnabled && (
                            <Chip
                              label="Deaktiviert"
                              size="small"
                              color="warning"
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {meta.when}
                        </Typography>
                      }
                    />

                    {/* Rechte Seite: Toggle + Nav-Pfeil */}
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{ flexShrink: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip
                        title={
                          isEnabled
                            ? "Automatischen Versand deaktivieren"
                            : "Automatischen Versand aktivieren"
                        }
                        placement="top"
                      >
                        <Switch
                          checked={isEnabled}
                          onChange={(e) =>
                            handleEnabledToggle(
                              meta.enabledKey,
                              e.target.checked,
                            )
                          }
                          size="small"
                          color="primary"
                        />
                      </Tooltip>
                    </Stack>

                    <Iconify
                      icon="mdi:chevron-right"
                      className="row-nav-icon"
                      sx={{
                        fontSize: 20,
                        color: "text.disabled",
                        flexShrink: 0,
                        transition: "transform 0.15s ease, color 0.15s ease",
                      }}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MailSettings;
